import axios from 'axios';
import { getTenantHeader } from '../context/TenantContext';
import { BUILD_VERSION } from './version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
    // EVENTSFLO-CLIENT-LOGIN-REAL-FAILED-ATTEMPT-BACKEND-TRACE-V1
    // Echo the running frontend build on every API request so backend
    // diagnostic logs can correlate failed auth attempts to the exact
    // browser bundle that made them. Cheap; no secrets.
    'X-Build-Version': BUILD_VERSION,
  },
  timeout: 30000, // 30 second timeout
});

// EF-PLATFORM-AUTHENTICATION-V1: Initialize token from localStorage on app load
const TOKEN_KEY = "eventsflo_auth_token";
const storedToken = (() => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
})();

if (storedToken) {
  api.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
}

// Retry configuration for handling server wake-up scenarios
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds between retries

// Helper to check if error is retryable
const isRetryableError = (error) => {
  // Retry on network errors (no response)
  if (!error.response) return true;
  // Retry on server errors (502, 503, 504)
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
};

// EF-SUPERADMIN-TENANT-SWITCH-V1: Request interceptor to add tenant context header
api.interceptors.request.use((config) => {
  config.retryCount = config.retryCount || 0;
  
  // Add X-Tenant-Context header for superadmin tenant switching
  const tenantContext = getTenantHeader();
  if (tenantContext) {
    config.headers['X-Tenant-Context'] = tenantContext;
  }
  
  return config;
});

// Response interceptor with retry logic and auth error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // EF-PLATFORM-AUTHENTICATION-V1: Handle 401 errors by redirecting to login
    // EF-CLIENT-ONBOARDING-FRONTEND-V1: Don't auto-redirect for client onboarding/auth endpoints
    if (error.response?.status === 401 && !config.url?.includes('/auth/')) {
      // Check if this is a client-specific endpoint that should NOT trigger auto-redirect
      const isClientOnboardingEndpoint = 
        config.url?.includes('/client/onboarding') ||
        config.url?.includes('/client/password-reset');
      
      if (isClientOnboardingEndpoint) {
        // Don't auto-redirect for client onboarding - let the page handle the error
        return Promise.reject(error);
      }
      
      // Check if this is a client API endpoint (authenticated client requests)
      const isClientApiEndpoint = config.url?.startsWith('/client/') && !isClientOnboardingEndpoint;
      
      // Clear stored auth data
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem("eventsflo_auth_user");
        // Also clear client token if it was a client endpoint
        if (isClientApiEndpoint) {
          localStorage.removeItem("eventsflo_client_token");
        }
      } catch {}
      delete api.defaults.headers.common["Authorization"];
      
      // Redirect to appropriate login page based on endpoint
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        if (isClientApiEndpoint || window.location.pathname.startsWith('/client')) {
          window.location.href = '/client/login';
        } else {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
    
    // Only retry GET requests and if within retry limit
    if (config.method === 'get' && config.retryCount < MAX_RETRIES && isRetryableError(error)) {
      config.retryCount += 1;
      console.log(`Retrying request (${config.retryCount}/${MAX_RETRIES}): ${config.url}`);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      
      return api(config);
    }
    
    return Promise.reject(error);
  }
);

/**
 * Extract error message from API response
 * Handles both string errors and Pydantic validation error arrays
 */
export const extractErrorMessage = (error) => {
  const data = error?.response?.data;
  
  if (!data) {
    return error?.message || 'An unexpected error occurred';
  }
  
  // Handle string detail
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  
  // Handle Pydantic validation errors (array of {type, loc, msg, input, url})
  if (Array.isArray(data.detail)) {
    return data.detail
      .map(err => {
        const field = err.loc?.slice(1).join('.') || 'field';
        return `${field}: ${err.msg}`;
      })
      .join('; ');
  }
  
  // Handle object detail
  if (typeof data.detail === 'object' && data.detail !== null) {
    return data.detail.msg || data.detail.message || JSON.stringify(data.detail);
  }
  
  // Fallback
  return data.message || data.error || 'Operation failed';
};

// ============== DASHBOARD ==============
export const getDashboardMetrics = () => api.get('/dashboard/metrics');

// ============== PDF ENGINE HEALTH (EF-PDF-HEALTH-CHECK) ==============
export const getPdfHealthCheck = () => api.get('/health/pdf');

// ============== CLIENTS ==============
export const getClients = (params) => api.get('/clients', { params });
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.patch(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// ============== CLIENT CONTACTS ==============
export const getClientContacts = (clientId, params) => api.get(`/clients/${clientId}/contacts`, { params });
export const createClientContact = (clientId, data) => api.post(`/clients/${clientId}/contacts`, data);
export const getClientContact = (contactId) => api.get(`/client-contacts/${contactId}`);
export const updateClientContact = (contactId, data) => api.patch(`/client-contacts/${contactId}`, data);
export const deleteClientContact = (contactId, hardDelete = false) => api.delete(`/client-contacts/${contactId}`, { params: { hard_delete: hardDelete } });

// ============== CLIENT ONBOARDING (EF-CLIENT-ONBOARDING-ADMIN-CONTROLS-V1) ==============
export const sendClientOnboardingLink = (contactId) => api.post(`/client-contacts/${contactId}/send-onboarding-link`);
export const getClientUsers = (clientId) => api.get(`/clients/${clientId}/users`);
export const clientPasswordResetRequest = (email) => api.post('/client/password-reset-request', { email });

// ============== CLIENT AUTH (EF-CLIENT-ONBOARDING-FRONTEND-V1) ==============
export const clientLogin = (email, password) => api.post('/client/auth/login', { email, password });
export const clientOnboardingComplete = (token, user_id, password) => api.post('/client/onboarding/complete', { token, user_id, password });
export const clientPasswordResetConfirm = (token, user_id, password) => api.post('/client/password-reset-confirm', { token, user_id, password });

// ============== CLIENT PORTAL API (EF-CLIENT-ROLE-FOUNDATION-V1) ==============
// These functions use the client portal token (different from admin token)
// They should be called with custom axios instance or direct fetch

// ============== INTAKE CONFLICT RESOLUTION (EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1) ==============
export const findIntakeMatches = (packetId) => api.post(`/intake-packets/${packetId}/find-matches`);
export const getIntakeConflicts = (packetId, staffId) => api.post(`/intake-packets/${packetId}/get-conflicts/${staffId}`);
export const resolveIntakeConflicts = (packetId, staffId, fieldDecisions) => api.post(`/intake-packets/${packetId}/resolve-conflicts/${staffId}`, { field_decisions: fieldDecisions });
export const getCrewAuditLog = (staffId, limit = 50) => api.get(`/crew/${staffId}/audit-log?limit=${limit}`);

// ============== VENUES ==============
export const getVenues = (params) => api.get('/venues', { params });
export const getVenue = (id) => api.get(`/venues/${id}`);
// EVENTSFLO-LIVE-DATA-ENTRY-VENUE-LOCATION-FILENAME-ADDRESS-UX-V1:
// `also_create_location` (default TRUE server-side) triggers auto-creation of
// a matching VenueLocation when a Venue is added. Callers can pass
// `{ alsoCreateLocation: false }` to skip.
export const createVenue = (data, { alsoCreateLocation } = {}) => {
  const params = {};
  if (typeof alsoCreateLocation === "boolean") {
    params.also_create_location = alsoCreateLocation ? "true" : "false";
  }
  return api.post('/venues', data, { params });
};
export const updateVenue = (id, data) => api.patch(`/venues/${id}`, data);
export const deleteVenue = (id) => api.delete(`/venues/${id}`);

// ============== VENUE LOCATIONS ==============
export const getVenueLocations = (params) => api.get('/venue-locations', { params });
export const getVenueLocation = (id) => api.get(`/venue-locations/${id}`);
export const createVenueLocation = (data) => api.post('/venue-locations', data);
export const updateVenueLocation = (id, data) => api.patch(`/venue-locations/${id}`, data);
export const deleteVenueLocation = (id) => api.delete(`/venue-locations/${id}`);

// EVENTSFLO-CLIENT-PROFILE-VENUE-LOCATIONS-AND-REMOVE-ASSOCIATION-V1
export const removeClientVenueAssociation = (clientId, venueId) =>
  api.delete(`/clients/${clientId}/venues/${venueId}`);

// ============== PROJECTS ==============
export const getProjects = (params) => api.get('/projects', { params });
// EF-PLATFORM-GLOBAL-AUDIT-MIGRATION-EXECUTION-V1: Unified v2 endpoint with server-side operations
export const getProjectsUnified = (params) => api.get('/v2/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.patch(`/projects/${id}`, data);
// Governed deletion with cascade and audit
export const getProjectDeletionImpact = (id) => api.get(`/projects/${id}/deletion-impact`);
export const deleteProject = (id, deletionReason) => {
  // PLATFORM LAW: deletion_reason is MANDATORY - never null, never empty
  if (!deletionReason || typeof deletionReason !== 'string' || deletionReason.length < 10) {
    return Promise.reject(new Error('Deletion reason must be at least 10 characters'));
  }
  return api.delete(`/projects/${id}`, { 
    data: { 
      deletion_reason: deletionReason 
    } 
  });
};

// ============== SHIFTS ==============
export const getShifts = (params) => api.get('/shifts', { params });
export const getShift = (id) => api.get(`/shifts/${id}`);
export const createShift = (data) => api.post('/shifts', data);
export const previewShiftRules = (data) => api.post('/shifts/preview-rules', data);
export const createShiftWithRules = (data) => api.post('/shifts/with-rules', data);
// EVENTSFLO-CALENDAR-CREATE-SHIFT-WITH-RULES-R2-FIX-V1
// One-shot rule-aware create for surfaces without a rule-prompt UI.
export const createShiftWithRulesAutoApply = (data) => api.post('/shifts/with-rules-auto-apply', data);
export const updateShift = (id, data) => api.patch(`/shifts/${id}`, data);
export const updateShiftWithRules = (id, data) => api.patch(`/shifts/${id}/with-rules`, data);
// Governed deletion with cascade and audit
export const getShiftDeletionImpact = (id) => api.get(`/shifts/${id}/deletion-impact`);
export const deleteShift = (id, deletionReason) => {
  // PLATFORM LAW: deletion_reason is MANDATORY - never null, never empty
  if (!deletionReason || typeof deletionReason !== 'string' || deletionReason.length < 10) {
    return Promise.reject(new Error('Deletion reason must be at least 10 characters'));
  }
  return api.delete(`/shifts/${id}`, { 
    data: { 
      deletion_reason: deletionReason 
    } 
  });
};
export const duplicateShift = (id, newDate = null) => 
  api.post(`/shifts/${id}/duplicate`, null, { params: newDate ? { new_date: newDate } : {} });
export const duplicateShiftMultiDay = (id, dates) => 
  api.post(`/shifts/${id}/duplicate-multi`, { dates });
export const recordShiftRuleAcceptances = (shiftId, acceptances) => 
  api.post(`/shifts/${shiftId}/rule-acceptances`, acceptances);

// ============== TASK DEFINITIONS ==============
export const getTaskDefinitions = (params) => api.get('/task-definitions', { params });
export const getTaskDefinition = (id) => api.get(`/task-definitions/${id}`);
export const createTaskDefinition = (data) => api.post('/task-definitions', data);
export const updateTaskDefinition = (id, data) => api.patch(`/task-definitions/${id}`, data);
export const deleteTaskDefinition = (id) => api.delete(`/task-definitions/${id}`);
// EVENTSFLO-TASK-DEFINITIONS-CSV-EXPORT-ALL-FIELDS-TAX-FIX-V1
// The endpoint requires admin auth, so we fetch with the axios client
// (Bearer token) as a blob and let the caller drive the download. A
// legacy `exportTaskDefinitionsCsv()` URL builder is retained for
// backwards compatibility but should not be used directly with
// `window.open` (that path silently produced a header-only CSV).
export const exportTaskDefinitionsCsv = () => `${API_BASE}/task-definitions/export/csv`;
export const downloadTaskDefinitionsCsv = () =>
  api.get('/task-definitions/export/csv', { responseType: 'blob' });

// EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1
// Toggle whether a task definition appears in new-shift assignment selectors.
// Does NOT delete or modify any pricing / historical line items.
export const setTaskDefinitionEnabled = (id, payload) =>
  api.post(`/admin/task-definitions/${id}/set-enabled`, payload);

// EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
// Admin-editable Financial Tax Rules Matrix endpoints. The matrix maps
// (ClientTaxTreatment × LineTaxClass) → VAT decision. Server already
// lazy-seeds platform defaults on first GET.
export const listFinancialTaxRules = (params) =>
  api.get('/admin/financial-tax-rules', { params });
export const createFinancialTaxRule = (data) =>
  api.post('/admin/financial-tax-rules', data);
export const updateFinancialTaxRule = (id, data) =>
  api.patch(`/admin/financial-tax-rules/${id}`, data);
export const deleteFinancialTaxRule = (id) =>
  api.delete(`/admin/financial-tax-rules/${id}`);
export const seedFinancialTaxRulesDefaults = () =>
  api.post('/admin/financial-tax-rules/seed-defaults');

// EVENTSFLO-TAX-CLASS-DEFAULTS-AND-GENERATION-PREFLIGHT-V1
export const getQuoteTaxPreflight = (projectId) =>
  api.get(`/projects/${projectId}/quote-tax-preflight`);

// EVENTSFLO-CLIENT-TAX-CODE-VALIDATION-MULTI-COUNTRY-V1
export const validateClientTaxCode = (clientId, payload = {}) =>
  api.post(`/clients/${clientId}/validate-tax-code`, payload);
export const applyClientTaxValidationSuggestions = (clientId, payload = {}) =>
  api.post(`/clients/${clientId}/apply-tax-validation-suggestions`, payload);


// ============== SHIFT TASKS ==============
export const getShiftTasks = (params) => api.get('/shift-tasks', { params });
export const getShiftTask = (id) => api.get(`/shift-tasks/${id}`);
export const createShiftTask = (data) => api.post('/shift-tasks', data);
export const updateShiftTask = (id, data) => api.patch(`/shift-tasks/${id}`, data);
export const deleteShiftTask = (id) => api.delete(`/shift-tasks/${id}`);
// PROMPT 68: Rule-driven task item operations
export const removeRuleTaskItem = (id, removalReason = null, acknowledgeWarning = false) => 
  api.delete(`/shift-tasks/${id}/rule-item`, { data: { removal_reason: removalReason, acknowledge_warning: acknowledgeWarning } });

// ============== SHIFT TASK LINE ITEMS (Phase 2) ==============
export const getShiftLineItems = (shiftId) => api.get(`/shifts/${shiftId}/line-items`);
export const getShiftLineItem = (id) => api.get(`/shift-task-line-items/${id}`);
export const createShiftLineItem = (data) => api.post('/shift-task-line-items', data);
export const updateShiftLineItem = (id, data) => api.patch(`/shift-task-line-items/${id}`, data);
export const deleteShiftLineItem = (id) => api.delete(`/shift-task-line-items/${id}`);
export const getShiftTravelDuration = (shiftId) => api.get(`/shifts/${shiftId}/travel-duration`);
export const changeTaskDefinition = (id, newTaskDefinitionId) => 
  api.patch(`/shift-tasks/${id}/change-definition`, null, { params: { new_task_definition_id: newTaskDefinitionId } });
export const getRuleTaskItems = (shiftId) => api.get(`/shifts/${shiftId}/rule-task-items`);
export const getRuleTaskItemLogs = (shiftId) => api.get(`/shifts/${shiftId}/rule-task-item-logs`);

// ============== TRAVEL ADVISOR (EF-TRAVEL-ADVISOR-01) ==============
export const getTravelAdvisory = (data) => api.post('/travel/advisor', data);

// EF-TRAVEL-ADVISOR-02: Test Google Maps API connection
export const testGoogleMapsConnection = () => api.post('/travel/advisor/test');

// EF-GOOGLE-KEY-HARDENING-01: Google Maps diagnostics and key management
export const getGoogleMapsDiagnostics = () => api.get('/travel/advisor/diagnostics');
export const getGoogleMapsStatus = () => api.get('/settings/platform/google-maps-status');
export const updateGoogleMapsApiKey = (apiKey) => api.put('/settings/platform/google-maps-api-key', { api_key: apiKey });
export const revealGoogleMapsKey = () => api.post('/settings/platform/reveal-google-key', { admin_confirmation: true });

// EF-GMAIL-SMTP-01: Email settings
export const getEmailSettings = () => api.get('/settings/platform/email');
export const updateEmailSettings = (data) => api.put('/settings/platform/email', data);
export const testSmtpConnection = (toEmail) => api.post('/email/test-smtp', { to_email: toEmail });

// ============== SHIFT CREW ASSIGNMENTS ==============
export const getShiftCrew = (shiftId) => api.get(`/shifts/${shiftId}/crew`);
// EF-ENFORCE-ACTIVE-CREW-ONLY-ASSIGNMENT-V1: Add includeInactive param
export const getEligibleShiftCrew = (shiftId, includeInactive = true) => 
  api.get(`/shifts/${shiftId}/eligible-crew`, { params: { include_inactive: includeInactive } });
// EF-CREW-CONFLICT-PREVENTION-V1: Check conflicts before assignment
export const checkShiftConflicts = (staffId, shiftId) => 
  api.get(`/assignments/check-conflicts/${staffId}/${shiftId}`);
// EF-CREW-CONFLICT-PREVENTION-V1: Updated to support conflict override
export const addShiftCrew = (shiftId, staffId, role = "Crew", notes = null, overrideConflicts = false, conflictOverrideReason = null, onboardingOverride = false) => 
  api.post(`/shifts/${shiftId}/crew`, { 
    staff_id: staffId, 
    role, 
    notes,
    override_conflicts: overrideConflicts,
    conflict_override_reason: conflictOverrideReason,
    onboarding_override: onboardingOverride  // EF-CREW-PLANNER-UX-AND-OVERRIDE-V1
  });
// EVENTSFLO-CREW-PLANNER-CONFIRMED-STATUS-AND-SHIFT-NAVIGATION-FIX-V1
// Overloaded signature: pass an object as `roleOrPatch` to send an
// arbitrary patch (e.g. `{ confirmed: true }` or `{ worked: true }`)
// without accidentally shipping `notes: {...}` to Pydantic (which
// used to fail with `notes: Input should be a valid string`).
// The 2-arg-style `updateShiftCrew(id, staffId, "Lead")` still works.
export const updateShiftCrew = (shiftId, staffId, roleOrPatch, notes = null) => {
  const body =
    roleOrPatch && typeof roleOrPatch === "object"
      ? roleOrPatch
      : { role: roleOrPatch, notes };
  return api.patch(`/shifts/${shiftId}/crew/${staffId}`, body);
};
export const removeShiftCrew = (shiftId, staffId) => 
  api.delete(`/shifts/${shiftId}/crew/${staffId}`);

// ============== TASK CREW ASSIGNMENTS ==============
export const getTaskCrew = (shiftTaskId) => api.get(`/shift-tasks/${shiftTaskId}/crew`);
// EF-ENFORCE-ACTIVE-CREW-ONLY-ASSIGNMENT-V1: Update to include inactive param
export const getEligibleCrew = (shiftTaskId, includeIneligible = true, includeInactive = true) => 
  api.get(`/shift-tasks/${shiftTaskId}/eligible-crew`, { 
    params: { include_ineligible: includeIneligible, include_inactive: includeInactive } 
  });
export const assignCrewToTask = (shiftTaskId, staffId, notes = null) => 
  api.post(`/shift-tasks/${shiftTaskId}/crew`, { staff_id: staffId, notes });
export const assignCrewBulk = (shiftTaskId, staffIds, notes = null) => 
  api.post(`/shift-tasks/${shiftTaskId}/crew/bulk`, { staff_ids: staffIds, notes });
export const removeCrewFromTask = (shiftTaskId, staffId) => 
  api.delete(`/shift-tasks/${shiftTaskId}/crew/${staffId}`);

// Crew member assignment endpoints
export const getCrewAssignments = (staffId, status = null) => 
  api.get(`/crew-assignments/${staffId}`, { params: status ? { status } : {} });
export const getCrewUnreadCount = (staffId) =>
  api.get(`/crew-assignments/${staffId}/unread-count`);

// Task crew assignment endpoints (for task-level assignments)
export const acceptTaskAssignment = (assignmentId, staffId) => 
  api.patch(`/task-crew-assignments/${assignmentId}/accept`, { staff_id: staffId });
export const rejectTaskAssignment = (assignmentId, staffId, rejectionReason = null) => 
  api.patch(`/task-crew-assignments/${assignmentId}/reject`, { staff_id: staffId, rejection_reason: rejectionReason });
export const markAssignmentAsSeen = (assignmentId, staffId) => 
  api.patch(`/task-crew-assignments/${assignmentId}/seen`, { staff_id: staffId });

// Shift crew assignment endpoints (for shift-level assignments from Shift Crew Planner)
export const acceptShiftAssignment = (shiftId, staffId) =>
  api.patch(`/shifts/${shiftId}/crew/${staffId}/accept`);
export const rejectShiftAssignment = (shiftId, staffId, rejectionReason = null) =>
  api.patch(`/shifts/${shiftId}/crew/${staffId}/reject`, null, { params: rejectionReason ? { rejection_reason: rejectionReason } : {} });
export const markShiftAssignmentSeen = (shiftId, staffId) =>
  api.patch(`/shifts/${shiftId}/crew/${staffId}/seen`);
// EVENTSFLO-CREW-PWA-WITHDRAW-AND-AUDIT-V1
export const withdrawShiftAssignment = (shiftId, staffId, reason) =>
  api.post(`/shifts/${shiftId}/crew/${staffId}/withdraw`, reason ? { reason } : {});
export const withdrawMyAssignment = (assignmentId, reason) =>
  api.post(`/crew/me/assignments/${assignmentId}/withdraw`, reason ? { reason } : {});

// EVENTSFLO-CREW-PWA-MESSAGE-CENTRE-V1
export const getCrewMessages = (staffId, onlyUnread = false, limit = 50) =>
  api.get(`/crew-messages/${staffId}`, { params: { only_unread: onlyUnread, limit } });
export const getCrewMessagesUnreadCount = (staffId) =>
  api.get(`/crew-messages/${staffId}/unread-count`);
export const markCrewMessageRead = (staffId, messageId) =>
  api.post(`/crew-messages/${staffId}/${messageId}/read`);
export const markAllCrewMessagesRead = (staffId) =>
  api.post(`/crew-messages/${staffId}/read-all`);
export const getAdminMessages = (onlyUnread = false, limit = 50) =>
  api.get(`/admin/messages`, { params: { only_unread: onlyUnread, limit } });
export const getAdminMessagesUnreadCount = () => api.get(`/admin/messages/unread-count`);
export const markAdminMessageRead = (messageId) =>
  api.post(`/admin/messages/${messageId}/read`);
export const markAllAdminMessagesRead = () => api.post(`/admin/messages/read-all`);

// Push to App - Admin endpoints
export const pushTaskToCrewMember = (assignmentId, sendEmail = true) =>
  api.post(`/task-crew-assignments/${assignmentId}/push`, { send_email: sendEmail });
export const pushTasksToCrewBulk = (assignmentIds, sendEmail = true) =>
  api.post('/task-crew-assignments/push-bulk', { assignment_ids: assignmentIds, send_email: sendEmail });
export const pushShiftTaskToCrew = (shiftId, staffId, sendEmail = true) =>
  api.post(`/shifts/${shiftId}/crew/${staffId}/push`, null, { params: { send_email: sendEmail } });
export const getTaskNotificationLogs = (params) =>
  api.get('/task-notification-logs', { params });

// ============== ASSIGNMENT BOARD ==============
export const getAssignmentBoard = (params) => api.get('/assignments/board', { params });
export const getAssignmentBoardProjects = () => api.get('/assignments/board/projects');
export const sendAssignmentReminder = (params) => api.post('/assignments/send-reminder', null, { params });

// ============== STAFF ==============
export const getStaffList = (params) => api.get('/staff', { params });
export const getStaff = (id) => api.get(`/staff/${id}`);
export const createStaff = (data) => api.post('/staff', data);
export const updateStaff = (id, data) => api.patch(`/staff/${id}`, data);
export const deleteStaff = (id) => api.delete(`/staff/${id}`);
export const sendCrewInvitation = (staffId) => api.post(`/staff/${staffId}/invite`);
// EVENTSFLO-CREW-PWA-LINK-SEND-AND-PENDING-ACTIONS-RESTORE-V1: PWA install link
export const sendStaffPWALink = (staffId) => api.post(`/staff/${staffId}/send-pwa-link`);
// EVENTSFLO-CREW-PLANNER-EMAIL-CREW-RESTORE-V1: Planner ad-hoc email to crew
export const sendShiftCrewEmail = (shiftId, staffId, payload) =>
  api.post(`/shifts/${shiftId}/crew/${staffId}/email`, payload);
export const sendBulkInvitations = (staffIds) => api.post('/staff/invite/bulk', staffIds);
export const trackPwaAccess = (staffId) => api.post(`/staff/${staffId}/pwa-access`);

// ============== ASSIGNMENTS ==============
export const getAssignments = (params) => api.get('/assignments', { params });
export const getAssignment = (id) => api.get(`/assignments/${id}`);
export const createAssignment = (data) => api.post('/assignments', data);
export const createAssignmentWithRules = (data) => api.post('/assignments/with-rules', data);
export const createAssignmentWithOverride = (data, overrideReason) => 
  api.post('/assignments/with-override', data, { params: { availability_override_reason: overrideReason } });
export const updateAssignment = (id, data) => api.patch(`/assignments/${id}`, data);
export const deleteAssignment = (id) => api.delete(`/assignments/${id}`);

// ============== STAFF CSV IMPORT ==============
export const previewStaffImport = (data) => api.post('/staff/import/preview', data);
export const executeStaffImport = (data) => api.post('/staff/import/execute', data);
export const getStaffImportLogs = (params) => api.get('/staff/import/logs', { params });
export const getStaffImportLog = (id) => api.get(`/staff/import/logs/${id}`);

// ============== STAFF AVAILABILITY ==============
export const getStaffAvailability = (params) => api.get('/staff-availability', { params });
export const getStaffAvailabilityById = (id) => api.get(`/staff-availability/${id}`);
export const createStaffAvailability = (data) => api.post('/staff-availability', data);
export const updateStaffAvailability = (id, data) => api.patch(`/staff-availability/${id}`, data);
export const deleteStaffAvailability = (id) => api.delete(`/staff-availability/${id}`);

// ============== RULES ==============
export const getRules = (params) => api.get('/rules', { params });
export const getRule = (id) => api.get(`/rules/${id}`);
export const createRule = (data) => api.post('/rules', data);
export const updateRule = (id, data) => api.patch(`/rules/${id}`, data);
export const deleteRule = (id) => api.delete(`/rules/${id}`);

// Rule Evaluation
export const evaluateShiftRules = (shiftData, acknowledgedWarnings = []) => 
  api.post('/rules/evaluate/shift', { shift_data: shiftData, acknowledged_warnings: acknowledgedWarnings });
export const evaluateAssignmentRules = (assignmentData, acknowledgedWarnings = []) => 
  api.post('/rules/evaluate/assignment', { assignment_data: assignmentData, acknowledged_warnings: acknowledgedWarnings });

// Rule Violation Logs
export const logRuleViolation = (data) => api.post('/rule-violation-logs', data);
export const getRuleViolationLogs = (params) => api.get('/rule-violation-logs', { params });
export const getEntityViolationLogs = (entityType, entityId) => 
  api.get(`/rule-violation-logs/entity/${entityType}/${entityId}`);

// Rule Variables
export const getRuleVariables = (params) => api.get('/rule-variables', { params });
export const updateRuleVariable = (key, data) => api.patch(`/rule-variables/${key}`, data);

// Task Rule Configs (EF-RULE-CONFIG-02)
export const getTaskRuleConfigs = (params) => api.get('/task-rule-configs', { params });
export const getTaskRuleConfig = (ruleKey) => api.get(`/task-rule-configs/${ruleKey}`);
export const updateTaskRuleConfig = (ruleKey, data) => api.patch(`/task-rule-configs/${ruleKey}`, data);

// ============== QUOTES ==============
export const getQuotes = (params) => api.get('/quotes', { params });
export const getQuote = (id) => api.get(`/quotes/${id}`);
// EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Get unified view model for UI and PDF
export const getQuoteViewModel = (id) => api.get(`/quotes/${id}/view-model`);
export const createQuote = (data) => api.post('/quotes', data);
export const generateQuoteFromProject = (data) => api.post('/quotes/from-project', data);
export const updateQuote = (id, data) => api.patch(`/quotes/${id}`, data);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);
export const getQuotePdfUrl = (id, outputMode = 'summary') => 
  `${API_BASE}/quotes/${id}/pdf?output_mode=${outputMode}`;
export const sendQuoteEmail = (id, data) => api.post(`/quotes/${id}/send-email`, data);
export const getQuoteEmailLog = (id) => api.get(`/quotes/${id}/email-log`);

// ============== INVOICES ==============
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
// EF-INVOICE-SYSTEM-ALIGNMENT-V1: View model endpoint
export const getInvoiceViewModel = (id) => api.get(`/invoices/${id}/view-model`);
export const createInvoiceFromQuote = (data) => api.post('/invoices/from-quote', data);
export const createInvoiceManual = (data) => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.patch(`/invoices/${id}`, data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const getInvoicePdfUrl = (id, outputMode = 'summary') => 
  `${API_BASE}/invoices/${id}/pdf?output_mode=${outputMode}`;

// EVENTSFLO-QUOTE-INVOICE-VISIBILITY-END-TO-END-DIAGNOSTIC-FIX-V1 (legacy)
// EVENTSFLO-CLIENT-PORTAL-DOCUMENT-VISIBILITY-AND-PDF-ACTIONS-FIX-V1
// Two separate authed helpers — never combine. View opens in a new tab;
// Download triggers a file save with an explicit filename.

export const viewAuthedAdminPdf = async (relativeUrl) => {
  const response = await api.get(relativeUrl, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const objectUrl = URL.createObjectURL(blob);
  const win = window.open(objectUrl, '_blank', 'noopener,noreferrer');
  if (!win) {
    // Popup blocked — surface to caller so it can toast/error.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 5_000);
    throw new Error('Popup blocked — please allow popups for this site');
  }
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

export const downloadAuthedAdminPdf = async (relativeUrl, filename) => {
  const response = await api.get(relativeUrl, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5_000);
};

// EVENTSFLO-SHIFT-INHERIT-PROJECT-VENUE-AND-QUOTE-FILENAME-FIX-V1
// Prefer the filename provided by the backend `Content-Disposition`
// header. Falls back to `fallbackFilename` only when the header is
// absent or unparsable. Used by Quote PDF downloads so the new
// `{Client}_Quote_{Number}.pdf` pattern (built server-side) reaches
// the user's Downloads folder unchanged. Deliberately NOT used for
// invoice downloads — those keep their existing behaviour where the
// frontend supplies `Invoice_{Number}.pdf`.
const _parseFilenameFromContentDisposition = (headerValue) => {
  if (!headerValue || typeof headerValue !== 'string') return null;
  // Try RFC-5987 encoded form first: `filename*=UTF-8''<pct-encoded>`.
  const enc = headerValue.match(/filename\*\s*=\s*[^']+'[^']*'([^;]+)/i);
  if (enc && enc[1]) {
    try {
      return decodeURIComponent(enc[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const m = headerValue.match(/filename\s*=\s*("([^"]+)"|([^;]+))/i);
  if (m) {
    return (m[2] || m[3] || '').trim();
  }
  return null;
};

export const downloadAuthedAdminPdfServerFilename = async (relativeUrl, fallbackFilename) => {
  const response = await api.get(relativeUrl, { responseType: 'blob' });
  const cd =
    response?.headers?.['content-disposition'] ||
    response?.headers?.['Content-Disposition'];
  const serverFilename = _parseFilenameFromContentDisposition(cd);
  const finalFilename = serverFilename || fallbackFilename || 'download.pdf';
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = finalFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5_000);
};

export const viewQuotePdf = (id, _qn, outputMode = 'summary') =>
  viewAuthedAdminPdf(`/quotes/${id}/pdf?output_mode=${outputMode}`);
// EVENTSFLO-SHIFT-INHERIT-PROJECT-VENUE-AND-QUOTE-FILENAME-FIX-V1:
// Trust the server-supplied filename (`{Client}_Quote_{Number}.pdf`).
// Fallback to legacy `Quote_{Number}.pdf` only if the header is missing.
export const downloadQuotePdf = (id, quoteNumber, outputMode = 'summary') =>
  downloadAuthedAdminPdfServerFilename(
    `/quotes/${id}/pdf?output_mode=${outputMode}`,
    `Quote_${quoteNumber || id}.pdf`,
  );

export const viewInvoicePdf = (id, _in, outputMode = 'summary') =>
  viewAuthedAdminPdf(`/invoices/${id}/pdf?output_mode=${outputMode}`);
export const downloadInvoicePdf = (id, invoiceNumber, outputMode = 'summary') =>
  downloadAuthedAdminPdf(`/invoices/${id}/pdf?output_mode=${outputMode}`, `Invoice_${invoiceNumber || id}.pdf`);

// EVENTSFLO-QUOTE-LIFECYCLE-CLIENT-ACCEPTANCE-ADMIN-SEND-V1
// EVENTSFLO-QUOTE-ACCEPTANCE-AUDIT-NOTES-V1
export const adminMarkQuoteAccepted = (id, note) => api.post(`/quotes/${id}/mark-accepted`, note ? { note } : {});
export const sendQuoteByEmailDefault = (id, recipient_email) =>
  api.post(`/quotes/${id}/send-email`, { recipient_email });

// Back-compat aliases kept until all callers migrate.
export const openQuotePdf = viewQuotePdf;
export const openInvoicePdf = viewInvoicePdf;
export const openAuthedAdminPdf = viewAuthedAdminPdf;
export const sendInvoiceEmail = (id, data) => api.post(`/invoices/${id}/send-email`, data);
export const getInvoiceEmailLog = (id) => api.get(`/invoices/${id}/email-log`);
export const getInvoiceCreditSummary = (id) => api.get(`/invoices/${id}/credit-summary`);
// EF-INVOICE-PAYMENT-TRACKING-V1: Payment endpoints
export const getInvoicePayments = (id) => api.get(`/invoices/${id}/payments`);
export const addInvoicePayment = (id, data) => api.post(`/invoices/${id}/payments`, data);
export const deleteInvoicePayment = (invoiceId, paymentId) => 
  api.delete(`/invoices/${invoiceId}/payments/${paymentId}`);

// ============== CREDIT NOTES (EF-CREDIT-NOTE-UI-V1) ==============
export const createCreditNoteFromInvoice = (invoiceId) => api.post(`/invoices/${invoiceId}/credit-notes`);
export const getInvoiceCreditNotes = (invoiceId) => api.get(`/invoices/${invoiceId}/credit-notes`);
export const getCreditNote = (id) => api.get(`/credit-notes/${id}`);
export const updateCreditNote = (id, data) => api.patch(`/credit-notes/${id}`, data);
export const updateCreditNoteLine = (creditNoteId, lineId, data) => 
  api.patch(`/credit-notes/${creditNoteId}/lines/${lineId}`, data);
export const issueCreditNote = (id) => api.post(`/credit-notes/${id}/issue`);
export const getCreditNotePdfUrl = (id) => `${API_BASE}/credit-notes/${id}/pdf`;

// EVENTSFLO-SECURITY-P1-QUOTE-INVOICE-READ-TENANT-SCOPE-V1:
// The credit-note PDF endpoint now requires auth. Anchor-tag opens
// (which drop the Authorization header) will 401. Use these helpers.
export const viewCreditNotePdf = (id) =>
  viewAuthedAdminPdf(`/credit-notes/${id}/pdf`);
export const downloadCreditNotePdf = (id, creditNoteNumber) =>
  downloadAuthedAdminPdf(`/credit-notes/${id}/pdf`, `CreditNote_${creditNoteNumber || id}.pdf`);

// ============== EMAIL TEMPLATES ==============
export const getEmailTemplates = (params) => api.get('/settings/email-templates', { params });
export const getEmailTemplate = (id) => api.get(`/settings/email-templates/${id}`);
export const getDefaultEmailTemplate = (templateType) => api.get(`/settings/email-templates/default/${templateType}`);
export const createEmailTemplate = (data) => api.post('/settings/email-templates', data);
export const updateEmailTemplate = (id, data) => api.patch(`/settings/email-templates/${id}`, data);
export const deleteEmailTemplate = (id) => api.delete(`/settings/email-templates/${id}`);
export const seedDefaultEmailTemplates = () => api.post('/settings/email-templates/seed-defaults');
export const resetDefaultEmailTemplate = (templateType) => api.post(`/settings/email-templates/reset-default/${templateType}`);
// EVENTSFLO-QUOTE-INVOICE-EMAIL-TEMPLATE-SETTINGS-VISIBILITY-AND-PORTAL-COPY-FIX-V1
export const getClientPortalUrl = (documentType) =>
  api.get('/settings/client-portal-url', { params: documentType ? { document_type: documentType } : {} });

// ============== PLATFORM SETTINGS ==============
export const getPlatformSettings = () => api.get('/settings/platform');
export const updatePlatformSettings = (data) => api.put('/settings/platform', data);
export const getAvailableCurrencies = () => api.get('/settings/platform/currencies');
export const getAvailableTimezones = () => api.get('/settings/platform/timezones');

// ============== DATA CLEANUP ==============
export const getEntityDependencies = (entityType) => api.get(`/cleanup/dependencies/${entityType}`);
export const getRecordDependencyDetails = (entityType, recordId) => 
  api.get(`/cleanup/dependencies/${entityType}/${recordId}`);
export const bulkUpdateEntities = (entityType, updates) => 
  api.patch(`/cleanup/bulk/${entityType}`, { updates });
export const bulkDeleteEntities = (entityType, ids, force = false) => 
  api.delete(`/cleanup/bulk/${entityType}`, { data: { ids, force } });

// PROMPT 67: Name Normalization Cleanup
export const previewNameNormalization = (table) => api.get(`/cleanup/names/preview/${table}`);
export const executeNameNormalization = (table, recordIds = null) => 
  api.post(`/cleanup/names/execute/${table}`, { record_ids: recordIds });

// ============== TASK CATEGORIES & SUBCATEGORIES (EF-TASK-TYPE-REFORM-03) ==============
export const getTaskCategories = () => api.get('/task-categories');
export const getTaskCategory = (id) => api.get(`/task-categories/${id}`);
export const createTaskCategory = (data) => api.post('/task-categories', data);
export const updateTaskCategory = (id, data) => api.patch(`/task-categories/${id}`, data);
export const deleteTaskCategory = (id) => api.delete(`/task-categories/${id}`);
export const getTaskCategoryUsage = (id) => api.get(`/task-categories/${id}/usage`);
export const getTaskSubCategories = (params) => api.get('/task-subcategories', { params });
export const getTaskSubCategory = (id) => api.get(`/task-subcategories/${id}`);
export const createTaskSubCategory = (data) => api.post('/task-subcategories', data);
export const updateTaskSubCategory = (id, data) => api.patch(`/task-subcategories/${id}`, data);
export const deleteTaskSubCategory = (id) => api.delete(`/task-subcategories/${id}`);
export const getTaskSubCategoryUsage = (id) => api.get(`/task-subcategories/${id}/usage`);

// ============== USER PREFERENCES ==============
export const getUserPreferences = () => api.get('/user/preferences');
export const updateUserPreferences = (data) => api.put('/user/preferences', data);
export const resetUserPreferences = () => api.delete('/user/preferences');
export const getFontScaleOptions = () => api.get('/user/preferences/font-scales');

// ============== PARSER ALIAS (EF-PARSER-ALIAS-TABLE-V1) ==============
export const getParserAliases = (params) => api.get('/parser-aliases', { params });
export const getParserAlias = (id) => api.get(`/parser-aliases/${id}`);
export const createParserAlias = (data) => api.post('/parser-aliases', data);
export const updateParserAlias = (id, data) => api.patch(`/parser-aliases/${id}`, data);
export const deleteParserAlias = (id) => api.delete(`/parser-aliases/${id}`);
export const seedDefaultParserAliases = () => api.post('/parser-aliases/seed-defaults');
export const resolveParserAlias = (text, aliasType) => 
  api.post(`/parser-aliases/resolve?text=${encodeURIComponent(text)}&alias_type=${encodeURIComponent(aliasType)}`);

// ============== VENUE IMPORT (Admin) ==============
export const importVenuesFromExcel = async (file, clientId, adminApiKey) => {
  const formData = new FormData();
  formData.append('file', file);
  
  return api.post(`/venues/import?client_id=${clientId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'X-Admin-API-Key': adminApiKey,
    },
    timeout: 60000, // 60 second timeout for large imports
  });
};

// ============== EF-DOCUMENT-ACCESS-READONLY-V1: Document Access ==============
export const getCrewDocuments = (crewId) => api.get(`/crew/${crewId}/documents`);
export const downloadDocument = (documentId) => api.get(`/documents/${documentId}/download`, { responseType: 'blob' });
export const getDocumentAccessLog = (documentId, limit = 50) => api.get(`/documents/${documentId}/access-log?limit=${limit}`);
export const getCrewDocumentsAccessLog = (crewId, limit = 100) => api.get(`/crew/${crewId}/documents/access-log?limit=${limit}`);

// ============== EVENTSFLO-TENANT-EDITABLE-EMAIL-TEMPLATES-BILINGUAL-V1 ==============
export const listPortalEmailTemplates = () =>
  api.get('/settings/portal-email-templates');
export const getPortalEmailTemplateMeta = () =>
  api.get('/settings/portal-email-templates/variables');
export const getPortalEmailTemplate = (key) =>
  api.get(`/settings/portal-email-templates/${key}`);
export const updatePortalEmailTemplate = (key, data) =>
  api.put(`/settings/portal-email-templates/${key}`, data);
export const resetPortalEmailTemplate = (key) =>
  api.post(`/settings/portal-email-templates/${key}/reset`);
export const previewPortalEmailTemplate = (key, data = {}) =>
  api.post(`/settings/portal-email-templates/${key}/preview`, data);
// EVENTSFLO-ACCESS-INVITE-EMAIL-TEMPLATES-SEND-TEST-V1
export const sendTestPortalEmailTemplate = (key, data = {}) =>
  api.post(`/settings/portal-email-templates/${key}/send-test`, data);

// EVENTSFLO-TASK-DEFINITIONS-PHASE-A-RENAME-CONFIRMATION-V1
export const listBilingualRenameCandidates = () =>
  api.get('/admin/task-definitions/bilingual-rename-candidates');
export const applyBilingualRenameCandidates = (items) =>
  api.post('/admin/task-definitions/bilingual-rename-candidates/apply', { items });

// EVENTSFLO-TASK-DEFINITIONS-EXPORT-DEPENDENCY-REPORT-V1
export const getTaskDefinitionDependencyReport = ({ include_system_seeds = true } = {}) =>
  api.get('/admin/task-definitions/export-dependency-report', {
    params: { include_system_seeds },
  });
export const downloadTaskDefinitionDependencyReportXlsx = ({ include_system_seeds = true } = {}) =>
  api.get('/admin/task-definitions/export-dependency-report.xlsx', {
    params: { include_system_seeds },
    responseType: 'blob',
  });

// EVENTSFLO-TASK-DEFINITIONS-IMPORT-DRY-RUN-PREVIEW-V1
export const taskDefinitionImportDryRun = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/task-definitions/import-dry-run', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
};

// EVENTSFLO-TASK-DEFINITIONS-IMPORT-APPLY-SAFE-CHANGES-V1
export const taskDefinitionImportApply = ({ file, selectedRowNumbers, confirmed }) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('selected_row_numbers', JSON.stringify(selectedRowNumbers || []));
  fd.append('confirmed', confirmed ? 'true' : 'false');
  return api.post('/admin/task-definitions/import-apply', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  });
};
export const taskDefinitionImportAudit = (limit = 50) =>
  api.get('/admin/task-definitions/import-audit', { params: { limit } });


// EVENTSFLO-SHIFT-RULE-STATUS-SHIELD-NON-BLOCKING-V1: read-only rule-status evaluator
export const getShiftRuleStatus = (shiftId) => api.get(`/shifts/${shiftId}/rule-status`);

// EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
export const getStaffPassportDetails = (staffId) =>
  api.get(`/staff/${staffId}/passport-details`);
export const patchStaffPassportDetails = (staffId, payload) =>
  api.patch(`/staff/${staffId}/passport-details`, payload);
// Returns the binary blob, caller must trigger download.
export const downloadProjectPassportExport = (projectId) =>
  api.get(`/projects/${projectId}/passport-id-export.xlsx`, { responseType: "blob" });
export const downloadShiftPassportExport = (shiftId) =>
  api.get(`/shifts/${shiftId}/passport-id-export.xlsx`, { responseType: "blob" });

// EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PORTAL-DOWNLOADS-V1
export const listCompanyDocuments = (params = {}) =>
  api.get("/admin/company-documents", { params });
export const getCompanyDocument = (id) =>
  api.get(`/admin/company-documents/${id}`);
export const uploadCompanyDocument = (formData) =>
  api.post("/admin/company-documents", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const updateCompanyDocument = (id, payload) =>
  api.patch(`/admin/company-documents/${id}`, payload);
export const deleteCompanyDocument = (id) =>
  api.delete(`/admin/company-documents/${id}`);
export const downloadCompanyDocument = (id) =>
  api.get(`/admin/company-documents/${id}/download`, { responseType: "blob" });

// Export api instance as named export for direct usage
export { api };

export default api;
